import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { InvoiceStatusBadge } from '@/components/admin/InvoiceStatusBadge'
import { InvoiceReminderModal } from '@/components/admin/InvoiceReminderModal'
import { InvoicePaymentThankYouModal } from '@/components/admin/InvoicePaymentThankYouModal'
import { useInvoices, useInvoicesStats, usePendingRemindersByInvoiceIds } from '@/hooks/useInvoices'
import { useDebounce } from '@/hooks/useDebounce'
import { formatCurrency, formatDate, cn } from '@/lib/utils'
import { downloadInvoicePDF } from '@/services/pdf'
import type { Invoice, InvoiceStatus } from '@/types/invoice'
import { QontoBalanceCard } from '@/components/admin/QontoBalanceCard'
import { QontoSyncBanner } from '@/components/admin/QontoSyncBanner'
import {
  Plus,
  Search,
  FileText,
  Eye,
  Download,
  Bell,
  Heart,
  Receipt,
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react'
import toast from 'react-hot-toast'

// Stat card component
interface StatCardProps {
  label: string
  value: string
  icon: React.ReactNode
  variant?: 'default' | 'success' | 'warning' | 'danger'
}

function StatCard({ label, value, icon, variant = 'default' }: StatCardProps) {
  const variantStyles = {
    default: 'bg-beige-50 text-brown-600',
    success: 'bg-mint-50 text-mint-600',
    warning: 'bg-gold-50 text-gold-600',
    danger: 'bg-red-50 text-red-600',
  }

  return (
    <div className={cn('rounded-xl p-4', variantStyles[variant])}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <div className="opacity-60">{icon}</div>
      </div>
    </div>
  )
}

// Invoice row component
interface InvoiceRowProps {
  facture: Invoice
  onDownload: (facture: Invoice) => void
  onReminder: (facture: Invoice) => void
  onThankYou: (facture: Invoice) => void
  hasPendingReminder: boolean
  hasPendingThankYou: boolean
}

function InvoiceRow({ facture, onDownload, onReminder, onThankYou, hasPendingReminder, hasPendingThankYou }: InvoiceRowProps) {
  const isOverdue =
    (facture.statut === 'en_attente' || facture.statut === 'sent') &&
    new Date(facture.date_echeance) < new Date()

  const displayStatus: InvoiceStatus = isOverdue ? 'overdue' : facture.statut

  return (
    <tr className="border-t border-beige-200 hover:bg-beige-50 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-gold-600" />
          </div>
          <div className="ml-4">
            <div className="font-mono text-sm font-medium text-brown-900">
              {facture.numero}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <p className="text-sm font-medium text-brown-900">
            {facture.client?.full_name || 'Client inconnu'}
          </p>
          {facture.client?.company && (
            <p className="text-xs text-brown-500">{facture.client.company}</p>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="text-sm font-medium text-brown-900">
          {formatCurrency(facture.total_ttc || 0)}
        </div>
        <div className="text-xs text-brown-400">
          HT: {formatCurrency(facture.total_ht || 0)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <InvoiceStatusBadge statut={displayStatus} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-brown-600">
        {formatDate(facture.date_creation)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <span className={cn(isOverdue ? 'text-red-600 font-medium' : 'text-brown-600')}>
          {formatDate(facture.date_echeance)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex justify-end gap-1">
          <Link to={`/invoices/${facture.id}`}>
            <Button variant="ghost" size="sm">
              <Eye className="w-4 h-4" />
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => onDownload(facture)}>
            <Download className="w-4 h-4" />
          </Button>
          {(facture.statut === 'en_attente' || facture.statut === 'sent' || isOverdue) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReminder(facture)}
              className="relative"
              title={hasPendingReminder ? 'Une relance est planifiée' : 'Envoyer une relance'}
            >
              <Bell className="w-4 h-4" />
              {hasPendingReminder && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
              )}
            </Button>
          )}
          {facture.statut === 'payee' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onThankYou(facture)}
              className="relative"
              title={hasPendingThankYou ? 'Un remerciement est planifié' : "Envoyer un remerciement et un lien d'avis Google"}
            >
              <Heart className="w-4 h-4" />
              {hasPendingThankYou && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-mint-500 ring-2 ring-white" />
              )}
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

// Pagination component
interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null

  return (
    <div className="flex items-center justify-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-sm text-brown-600">
        Page {currentPage} sur {totalPages}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  )
}

// Status filter options
const statusOptions: { value: InvoiceStatus | ''; label: string }[] = [
  { value: '', label: 'Tous les statuts' },
  { value: 'draft', label: 'Brouillons' },
  { value: 'sent', label: 'Envoyees' },
  { value: 'en_attente', label: 'En attente' },
  { value: 'payee', label: 'Payees' },
  { value: 'overdue', label: 'En retard' },
  { value: 'cancelled', label: 'Annulees' },
]

export default function InvoicesPage() {
  const [search, setSearch] = useState('')
  const [statutFilter, setStatutFilter] = useState<InvoiceStatus | null>(null)
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('date_creation')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [factureToRemind, setFactureToRemind] = useState<Invoice | null>(null)
  const [factureToThank, setFactureToThank] = useState<Invoice | null>(null)

  const debouncedSearch = useDebounce(search, 300)

  const { invoices, total, isLoading } = useInvoices({
    search: debouncedSearch,
    statut: statutFilter,
    page,
    limit: 20,
    sortBy,
    sortOrder,
  })

  const invoiceIds = useMemo(() => invoices.map((f) => f.id), [invoices])
  const { data: pendingCounts } = usePendingRemindersByInvoiceIds(invoiceIds, 'reminder')
  const { data: pendingThankYouCounts } = usePendingRemindersByInvoiceIds(invoiceIds, 'thank_you')

  function handleSort(col: string) {
    if (sortBy === col) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col)
      setSortOrder('desc')
    }
    setPage(1)
  }

  function SortIcon({ col }: { col: string }) {
    if (sortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
    return sortOrder === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 text-mint-600" />
      : <ChevronDown className="w-3.5 h-3.5 text-mint-600" />
  }

  const { data: stats, isLoading: statsLoading } = useInvoicesStats()

  const totalPages = useMemo(() => Math.ceil(total / 20), [total])

  const handleDownload = async (facture: Invoice) => {
    try {
      await downloadInvoicePDF(facture)
      toast.success('PDF telecharge')
    } catch {
      toast.error('Erreur lors du telechargement')
    }
  }

  const handleReminder = (facture: Invoice) => {
    setFactureToRemind(facture)
  }

  const handleThankYou = (facture: Invoice) => {
    setFactureToThank(facture)
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Factures
          </h1>
          <p className="mt-1 text-brown-600">
            {total} facture{total > 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/invoices/new">
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            Nouvelle facture
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <QontoBalanceCard />
        <StatCard
          label="Total facture"
          value={statsLoading ? '...' : formatCurrency(stats?.total ?? 0)}
          icon={<Receipt className="w-6 h-6" />}
        />
        <StatCard
          label="Payees"
          value={statsLoading ? '...' : formatCurrency(stats?.payees ?? 0)}
          icon={<CheckCircle className="w-6 h-6" />}
          variant="success"
        />
        <StatCard
          label="En attente"
          value={statsLoading ? '...' : formatCurrency(stats?.enAttente ?? 0)}
          icon={<Clock className="w-6 h-6" />}
          variant="warning"
        />
        <StatCard
          label="En retard"
          value={statsLoading ? '...' : formatCurrency(stats?.enRetard ?? 0)}
          icon={<AlertTriangle className="w-6 h-6" />}
          variant="danger"
        />
      </div>

      {/* Qonto sync */}
      <QontoSyncBanner type="invoices" />

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Rechercher par numero..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <select
            value={statutFilter || ''}
            onChange={(e) => {
              setStatutFilter(e.target.value as InvoiceStatus || null)
              setPage(1)
            }}
            className="px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* Invoice list */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12 text-brown-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-beige-300" />
            <p className="font-medium">Aucune facture trouvee</p>
            <p className="text-sm mt-1">
              {search || statutFilter
                ? 'Essayez avec d\'autres criteres de recherche'
                : 'Commencez par creer votre premiere facture'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-beige-50 border-b border-beige-200">
                <tr>
                  {([
                    { label: 'Numero', col: 'numero', align: 'left' as const },
                    { label: 'Client', col: null, align: 'left' as const },
                    { label: 'Montant TTC', col: 'total_ttc', align: 'right' as const },
                    { label: 'Statut', col: 'statut', align: 'left' as const },
                    { label: 'Date', col: 'date_creation', align: 'left' as const },
                    { label: 'Echeance', col: 'date_echeance', align: 'left' as const },
                    { label: 'Actions', col: null, align: 'right' as const },
                  ]).map(({ label, col, align }) => (
                    <th
                      key={label}
                      className={`text-${align} px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider whitespace-nowrap ${col ? 'cursor-pointer select-none hover:text-brown-700' : ''}`}
                      onClick={col ? () => handleSort(col) : undefined}
                    >
                      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
                        {label}
                        {col && <SortIcon col={col} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-200">
                {invoices.map((facture) => (
                  <InvoiceRow
                    key={facture.id}
                    facture={facture}
                    onDownload={handleDownload}
                    onReminder={handleReminder}
                    onThankYou={handleThankYou}
                    hasPendingReminder={!!pendingCounts?.[facture.id]}
                    hasPendingThankYou={!!pendingThankYouCounts?.[facture.id]}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <InvoiceReminderModal
        invoice={factureToRemind}
        isOpen={!!factureToRemind}
        onClose={() => setFactureToRemind(null)}
      />

      <InvoicePaymentThankYouModal
        invoice={factureToThank}
        isOpen={!!factureToThank}
        onClose={() => setFactureToThank(null)}
      />
    </div>
  )
}
